// Thin client over the Phase 3 printing API (`/api/print/*`). The browser
// only ever talks to the Rudras-AI backend: it never opens a socket to a
// printer, never probes a LAN address, and never sends a token anywhere
// but the Authorization header the shared axios instance adds.
import api from "../api";

const unwrap = (p) => p.then((r) => r.data);

export const printingApi = {
  agents: () => unwrap(api.get("/print/agents")),
  issueAgent: (body) => unwrap(api.post("/print/agents", body)),
  rotateAgent: (id) => unwrap(api.post(`/print/agents/${encodeURIComponent(id)}/rotate`)),
  revokeAgent: (id) => unwrap(api.delete(`/print/agents/${encodeURIComponent(id)}`)),

  profiles: () => unwrap(api.get("/print/profiles")),
  printers: () => unwrap(api.get("/print/printers")),
  createPrinter: (body) => unwrap(api.post("/print/printers", body)),
  patchPrinter: (id, body) => unwrap(api.patch(`/print/printers/${encodeURIComponent(id)}`, body)),
  removePrinter: (id) => unwrap(api.delete(`/print/printers/${encodeURIComponent(id)}`)),

  config: () => unwrap(api.get("/print/config")),
  setBusiness: (body) => unwrap(api.put("/print/config/business", body)),
  // Phase 6 — enable/disable automatic customer receipts for THIS tenant.
  // The server stamps the activation time; the browser never sends one.
  setAutomaticReceipts: (action) => unwrap(api.post("/print/config/automatic-receipts", { action })),
  setAutomaticPackingSlips: (action) => unwrap(api.post("/print/config/automatic-packing-slips", { action })),
  finishPackingSlipSetup: () => unwrap(api.post("/print/config/automatic-packing-slips/finish-setup")),
  packingSlipAttention: () => unwrap(api.get("/print/automatic-packing-slips/attention")),
  dismissPackingSlip: (orderId) => unwrap(api.post(`/print/automatic-packing-slips/orders/${encodeURIComponent(orderId)}/dismiss`)),
  documents: (catalogId) => unwrap(api.get("/print/config/documents", { params: catalogId ? { catalog_id: catalogId } : {} })),
  putDocumentDefaults: (docType, body) => unwrap(api.put(`/print/config/documents/${encodeURIComponent(docType)}`, body)),
  catalogOverrides: (catalogId) => unwrap(api.get(`/print/catalogs/${encodeURIComponent(catalogId)}/overrides`)),
  putCatalogOverride: (catalogId, docType, body) =>
    unwrap(api.put(`/print/catalogs/${encodeURIComponent(catalogId)}/overrides/${encodeURIComponent(docType)}`, body)),
  clearCatalogOverride: (catalogId, docType, fields) =>
    unwrap(api.delete(`/print/catalogs/${encodeURIComponent(catalogId)}/overrides`, {
      params: { ...(docType ? { doc_type: docType } : {}), ...(fields && fields.length ? { fields: fields.join(",") } : {}) },
    })),

  rules: (catalogId) => unwrap(api.get("/print/rules", { params: catalogId ? { catalog_id: catalogId } : {} })),
  createRule: (body) => unwrap(api.post("/print/rules", body)),
  patchRule: (id, body) => unwrap(api.patch(`/print/rules/${encodeURIComponent(id)}`, body)),
  deleteRule: (id) => unwrap(api.delete(`/print/rules/${encodeURIComponent(id)}`)),

  layoutSections: () => unwrap(api.get("/print/layouts/sections")),
  layouts: (docType, includeHistory = true) =>
    unwrap(api.get("/print/layouts", { params: { ...(docType ? { doc_type: docType } : {}), include_history: includeHistory } })),
  createLayoutVersion: (docType, body) => unwrap(api.post(`/print/layouts/${encodeURIComponent(docType)}`, body)),
  effective: (docType, catalogId) =>
    unwrap(api.get("/print/effective", { params: { ...(docType ? { doc_type: docType } : {}), ...(catalogId ? { catalog_id: catalogId } : {}) } })),
  preview: (body) => unwrap(api.post("/print/preview", body)),

  jobs: (params = {}) => unwrap(api.get("/print/jobs", { params: { limit: 200, ...params } })),
  job: (id) => unwrap(api.get(`/print/jobs/${encodeURIComponent(id)}`)),
  reprint: (id, body) => unwrap(api.post(`/print/jobs/${encodeURIComponent(id)}/reprint`, body)),
  cancelJob: (id, reason) => unwrap(api.post(`/print/jobs/${encodeURIComponent(id)}/cancel`, reason ? { reason } : {})),
  testPrint: (body) => unwrap(api.post("/print/test-print", body)),

  catalogs: () => unwrap(api.get("/retail/catalogs")),
};

export default printingApi;
